import Phaser from 'phaser';
import type { TilesetId } from '../config/tilesets.js';
import { TILESET_ROOTS, DEFAULT_TILESET } from '../config/tilesets.js';
import { drawParchmentBubble, drawWoodDoor, KNOB_GOLD } from './chrome.js';

/**
 * World-kit PROPS — every placeable object the plaza world is made of, served
 * from one module for BOTH render targets:
 *
 *  - **DOM (the plaza):** `propUrl(tileset, file)` gives the object png URL for
 *    `<img>` / CSS composition (trees, bushes, crates, signs).
 *  - **Canvas (stages):** `preloadProps` + `addCrate` / `addSign` / `addDoor` /
 *    `drawDecor` place the SAME pngs (or chrome-painted door) as Phaser objects.
 *
 * Decorative/render only — props never collide; gameplay positions stay
 * server-authoritative. The sunnyland + icezone regions ship the same filenames.
 */

/** Object pngs under a region's `objects/` used by the kit. */
export const CRATE_FILE = 'Crate.png';
export const SIGN_FILES = ['Sign_1.png', 'Sign_2.png'] as const;
const TREE_FILES = ['Tree_2.png', 'Tree_3.png'] as const;
const BUSH_FILES = ['Bush_1.png', 'Bush_2.png', 'Bush_3.png', 'Mushroom_1.png'] as const;

/** Public URL of an object png — the DOM (plaza) side of the props kit. */
export function propUrl(tileset: TilesetId, file: string): string {
  return `${TILESET_ROOTS[tileset]}/objects/${file}`;
}

/** Phaser texture key for an object png (per region). */
export const propKey = (tileset: TilesetId, file: string): string => `prop-${tileset}-${file}`;

/**
 * Queue every kit prop texture (crate, signs, trees, bushes) for a region. Call
 * from a scene's `preload()` (idempotent — Phaser skips loaded keys).
 */
export function preloadProps(scene: Phaser.Scene, tileset: TilesetId = DEFAULT_TILESET): void {
  for (const file of [CRATE_FILE, ...SIGN_FILES, ...TREE_FILES, ...BUSH_FILES]) {
    const key = propKey(tileset, file);
    if (!scene.textures.exists(key)) {
      scene.load.image(key, propUrl(tileset, file));
    }
  }
}

// -- crate --------------------------------------------------------------------

/** The plaza crate png as a Phaser image (caller positions + sizes it). */
export function addCrate(
  scene: Phaser.Scene,
  tileset: TilesetId = DEFAULT_TILESET,
): Phaser.GameObjects.Image {
  return scene.add.image(0, 0, propKey(tileset, CRATE_FILE));
}

// -- sign ---------------------------------------------------------------------

export interface SignPropOptions {
  /** Screen-x (px) of the sign centre. */
  x: number;
  /** Screen-y (px) the sign's base rests on. */
  baseY: number;
  /** On-screen sign height (px); width keeps the art ratio. */
  height?: number;
  /** Alternates the art: even = board (Sign_1, gets the "?" glyph), odd = arrow. */
  index?: number;
  tileset?: TilesetId;
}

/**
 * A plaza sign rooted by its base, with the "?" hint glyph on board-style signs
 * so they read as interactive. Pair with `createHintBubble` for the copy.
 */
export function addSign(
  scene: Phaser.Scene,
  opts: SignPropOptions,
): Phaser.GameObjects.GameObject[] {
  const { x, baseY, height = 60, index = 0 } = opts;
  const tileset = opts.tileset ?? DEFAULT_TILESET;
  const file = SIGN_FILES[index % SIGN_FILES.length];
  const out: Phaser.GameObjects.GameObject[] = [];
  const img = scene.add.image(x, baseY, propKey(tileset, file)).setOrigin(0.5, 1);
  img.setDisplaySize((img.width / img.height) * height, height);
  out.push(img);
  if (index % SIGN_FILES.length === 0) {
    out.push(
      scene.add
        .text(x, baseY - height * 0.66, '?', {
          fontFamily: 'Fredoka, sans-serif',
          fontSize: '18px',
          color: '#fff7e6',
          fontStyle: 'bold',
        })
        .setOrigin(0.5),
    );
  }
  return out;
}

/** A reusable parchment hint bubble (sign copy / tips) the host repositions. */
export interface HintBubbleHandle {
  container: Phaser.GameObjects.Container;
  /** Re-lay the bubble background to fit new (wrapped) copy. */
  setText(copy: string): void;
}

/**
 * One parchment speech bubble (plaza panel language, tail down) for proximity
 * sign copy. Hidden until the host positions + shows it.
 */
export function createHintBubble(scene: Phaser.Scene, wrapWidth = 180): HintBubbleHandle {
  const bg = scene.add.graphics();
  const text = scene.add
    .text(0, 0, '', {
      fontFamily: 'Fredoka, sans-serif',
      fontSize: '13px',
      color: '#3a2a16',
      align: 'center',
      wordWrap: { width: wrapWidth },
    })
    .setOrigin(0.5);
  const container = scene.add.container(0, 0, [bg, text]).setDepth(900);
  container.setVisible(false);
  return {
    container,
    setText(copy: string): void {
      text.setText(copy);
      const w = Math.ceil(text.width) + 24;
      const h = Math.ceil(text.height) + 18;
      text.setPosition(0, -h / 2);
      bg.clear();
      drawParchmentBubble(bg, -w / 2, -h, w, h, 10, true);
    },
  };
}

// -- door ---------------------------------------------------------------------

export interface DoorPropOptions {
  /** Screen-x (px) of the door centre. */
  x: number;
  /** Screen-y (px) of the door's base (the ledge it stands on). */
  baseY: number;
  w?: number;
  h?: number;
}

/**
 * The goal door in the plaza's wood-frame language (grained arch + parchment
 * inset + gold knob, painted by the chrome kit).
 */
export function addDoor(
  scene: Phaser.Scene,
  opts: DoorPropOptions,
): Phaser.GameObjects.Container {
  const { x, baseY, w = 64, h = 92 } = opts;
  const frame = scene.add.graphics();
  drawWoodDoor(frame, w, h);
  const knob = scene.add.circle(8, -h / 2, 5, KNOB_GOLD);
  return scene.add.container(x, baseY, [frame, knob]);
}

// -- decor (trees / bushes / mushrooms) ----------------------------------------

/** One decorative prop placed in screen-space (its base sits on `baseY`). */
export interface DecorProp {
  /** Screen-x (px) of the prop centre. */
  x: number;
  /** Screen-y (px) the prop's base (feet/roots) rests on. */
  baseY: number;
  /** On-screen height of the prop (px); width scales to keep the art's ratio. */
  height: number;
  /** 'tree' (taller, behind) or 'bush' (shorter, foreground filler). */
  kind: 'tree' | 'bush';
}

export interface DecorOptions {
  tileset?: TilesetId;
  /** Container the props are added to (caller controls depth/order). */
  layer: Phaser.GameObjects.Container;
}

/**
 * Draw decorative props (trees behind, bushes in front) into `layer`. Each is
 * rooted by its base on `baseY` (origin 0.5,1) — the same rooted look as the
 * plaza's `<img>`s. Deterministic per-prop art variety (no randomness).
 */
export function drawDecor(
  scene: Phaser.Scene,
  props: DecorProp[],
  opts: DecorOptions,
): Phaser.GameObjects.Image[] {
  const tileset = opts.tileset ?? DEFAULT_TILESET;
  const out: Phaser.GameObjects.Image[] = [];
  let i = 0;
  for (const p of props) {
    const file =
      p.kind === 'tree' ? TREE_FILES[i % TREE_FILES.length] : BUSH_FILES[i % BUSH_FILES.length];
    const img = scene.add.image(p.x, p.baseY, propKey(tileset, file)).setOrigin(0.5, 1);
    img.setScale(p.height / (img.height || 1));
    opts.layer.add(img);
    out.push(img);
    i += 1;
  }
  return out;
}
